/**
 * Formatting helpers shared by the Analytics cards.
 *
 * These live in one place because the dashboard's job is to not misrepresent the
 * numbers underneath it, and three of these functions encode a decision about how
 * to do that honestly — see `formatChange` and `formatDuration` in particular.
 */

export function toNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

/** 1234 → "1.2K". Zero and non-numbers render as "0", never as "—" or blank. */
export function formatCompact(value) {
  const number = toNumber(value);
  if (number <= 0) return "0";
  if (number >= 1_000_000) return `${(number / 1_000_000).toFixed(1)}M`;
  if (number >= 1_000) return `${(number / 1_000).toFixed(1)}K`;
  return String(Math.round(number));
}

/** Coins are whole units; never show a fractional coin. */
export function formatCoins(value) {
  return formatCompact(Math.round(toNumber(value)));
}

/**
 * USD with enough precision to not round a real cost down to "$0.00".
 *
 * Sub-cent totals are common here — a handful of cheap calls genuinely costs
 * $0.0004 — and "$0.00" reads as free.
 */
export function formatUsd(value) {
  const number = toNumber(value);
  if (number === 0) return "$0";
  if (number < 0.01) return `$${number.toFixed(4)}`;
  if (number < 1000) return `$${number.toFixed(2)}`;
  return `$${formatCompact(number)}`;
}

/**
 * Seconds → a duration a human reads at a glance.
 *
 * Null means "no completed runs to measure", which is not the same as zero and
 * must not render as "0s".
 */
export function formatDuration(seconds) {
  if (seconds == null || !Number.isFinite(Number(seconds))) return "—";
  const total = Math.max(0, Number(seconds));
  if (total < 1) return "<1s";
  if (total < 60) return `${Math.round(total)}s`;
  if (total < 3600) {
    const minutes = Math.floor(total / 60);
    const rest = Math.round(total % 60);
    return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
  }
  const hours = Math.floor(total / 3600);
  const minutes = Math.round((total % 3600) / 60);
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

export function formatPct(value, digits = 1) {
  return `${toNumber(value).toFixed(digits)}%`;
}

/**
 * A Delta's change_pct, as text.
 *
 * `change_pct` is null — not 0 — when the previous period had nothing to compare
 * against. The backend is deliberate about this, and rendering it as "0%" would
 * turn "brand new" into "flat", which is the opposite reading. So null becomes
 * "New", and only a real number gets a signed percentage.
 */
export function formatChange(changePct) {
  if (changePct == null || !Number.isFinite(Number(changePct))) return null;
  const number = Number(changePct);
  const sign = number >= 0 ? "+" : "";
  return `${sign}${number.toFixed(1)}%`;
}

/** Relative time for a past instant: "3m ago", "2h ago", "5d ago". */
export function formatRelative(value) {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const minutes = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
